import React, { useEffect, useRef, useState } from 'react';
import { usePayPalScriptReducer } from '@paypal/react-paypal-js';

// Google Pay ortamı: sandbox backend ile TEST, canlı ile PRODUCTION olmalı.
const GPAY_ENV = process.env.REACT_APP_PAYPAL_ENV || 'PRODUCTION';

// Google'ın pay.js'ini tek sefer yükle.
let payJsPromise;
const loadPayJs = () => {
  if (window.google?.payments?.api) return Promise.resolve();
  if (payJsPromise) return payJsPromise;
  payJsPromise = new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = 'https://pay.google.com/gp/p/js/pay.js';
    s.async = true;
    s.onload = resolve;
    s.onerror = reject;
    document.head.appendChild(s);
  });
  return payJsPromise;
};

const GooglePayButton = (props) => {
  const [{ isResolved }] = usePayPalScriptReducer();
  const containerRef = useRef(null);
  const propsRef = useRef(props);
  propsRef.current = props;
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!isResolved || !propsRef.current.amount) return undefined;
    let cancelled = false;
    let client;

    // Google ödeme yetkilendirme callback'i: order oluştur → confirm → capture.
    const onPaymentAuthorized = async (paymentData) => {
      const p = propsRef.current;
      try {
        const { orderId } = await p.createOrder();
        const confirm = await window.paypal
          .Googlepay()
          .confirmOrder({ orderId, paymentMethodData: paymentData.paymentMethodData });

        if (confirm.status === 'PAYER_ACTION_REQUIRED') {
          await window.paypal.Googlepay().initiatePayerAction({ orderId });
        }
        if (confirm.status === 'APPROVED' || confirm.status === 'PAYER_ACTION_REQUIRED') {
          const ok = await p.captureAndFinalize(orderId);
          return ok
            ? { transactionState: 'SUCCESS' }
            : { transactionState: 'ERROR', error: { intent: 'PAYMENT_AUTHORIZATION', message: 'capture_failed', reason: 'OTHER_ERROR' } };
        }
        return { transactionState: 'ERROR', error: { intent: 'PAYMENT_AUTHORIZATION', message: 'not_approved', reason: 'OTHER_ERROR' } };
      } catch (err) {
        p.onError?.(err.message);
        return { transactionState: 'ERROR', error: { intent: 'PAYMENT_AUTHORIZATION', message: err.message || 'error', reason: 'OTHER_ERROR' } };
      }
    };

    (async () => {
      try {
        await loadPayJs();
        if (cancelled) return;
        const gp = window.paypal?.Googlepay?.();
        if (!gp) return;
        const config = await gp.config();
        if (cancelled || !config?.isEligible) return;

        client = new window.google.payments.api.PaymentsClient({
          environment: GPAY_ENV,
          paymentDataCallbacks: { onPaymentAuthorized },
        });

        const ready = await client.isReadyToPay({
          apiVersion: config.apiVersion,
          apiVersionMinor: config.apiVersionMinor,
          allowedPaymentMethods: config.allowedPaymentMethods,
        });
        if (cancelled || !ready.result) return;

        const button = client.createButton({
          buttonSizeMode: 'fill',
          buttonColor: 'black',
          buttonType: 'pay',
          buttonRadius: 10,
          onClick: () => {
            const p = propsRef.current;
            if (p.disabled || !p.amount) return;
            client
              .loadPaymentData({
                apiVersion: config.apiVersion,
                apiVersionMinor: config.apiVersionMinor,
                allowedPaymentMethods: config.allowedPaymentMethods,
                merchantInfo: config.merchantInfo,
                transactionInfo: {
                  countryCode: config.countryCode || 'DE',
                  currencyCode: p.currency,
                  totalPriceStatus: 'FINAL',
                  totalPrice: String(p.amount),
                },
                callbackIntents: ['PAYMENT_AUTHORIZATION'],
              })
              .catch((err) => {
                // Kullanıcı ödeme sheet'ini kapattıysa (CANCELED) normaldir,
                // sessiz geç; gerçek bir hatayı ise logla.
                if (err?.statusCode !== 'CANCELED') {
                  console.warn('Google Pay ödeme akışı hatası:', err);
                }
              });
          },
        });

        if (containerRef.current && !cancelled) {
          containerRef.current.innerHTML = '';
          containerRef.current.appendChild(button);
          setVisible(true);
        }
      } catch (err) {
        /* uygunluk/yükleme başarısız → düğmeyi gizle */
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isResolved]);

  return <div ref={containerRef} className={`wallet-button gpay ${visible ? 'visible' : ''}`} />;
};

export default GooglePayButton;
